import React, { useState } from "react";
import { Row, Col, Button, Form, Input, Select, notification } from "antd";
import styles from "../MemberProfile.module.scss";
import textStyles from "../../../Shared/Styles/TextStyles.module.scss";
import buttonStyles from "../../../Shared/Styles/ButtonStyles.module.scss";
import { InputMask } from "@mtm/components";
import { useServiceUserCreationCtx } from "../../../../context/ServiceUserCreationContext";
import dropDownIcon from "../../../../assets/icons/downOutlined.svg";
import {
  TC_InitialValues,
  TrustedContact,
} from "../../../../types/ProfileTypes";
import { isEmailValid } from "../../../../helpers/ValidationHelpers";
import { UpsertTrustedContactDetails } from "../../../../services";
import DuplicateTrustedContactModal from "../../../CreatePerson/Modals/DuplicateTrustedContactModal";
import { IServiceUserInfo } from "../../../../types/ServiceUserInfo";
import { isValidPhone } from "../../../../helpers/personHelpers";

type Props = {
  profile: IServiceUserInfo;
  initialValues: TC_InitialValues;
  trustedContacts: TrustedContact[];
  updateTrustedContactsOnAddOrUpdate: (newTC: TrustedContact) => void;
  handleOnClose: () => void;
};

function TrustedContactForm({
  profile,
  initialValues,
  trustedContacts,
  updateTrustedContactsOnAddOrUpdate,
  handleOnClose,
}: Props) {
  const [shouldDisableBtn, setShouldDisableBtn] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDuplicateTCModalVisible, setIsDuplicateTCModalVisible] = useState<
    boolean
  >(false);

  const [form] = Form.useForm();
  const { relationshipTypesCtx } = useServiceUserCreationCtx();
  const [relationshipTypes] = relationshipTypesCtx;

  async function handleSubmitForm() {
    try {
      setIsLoading(true);
      const {
        firstName,
        lastName,
        email,
        phone,
        relationship,
        personAttributesKeyId,
      } = form.getFieldsValue([
        "firstName",
        "lastName",
        "phone",
        "email",
        "relationship",
        "personAttributesKeyId",
      ]);

      const trustedContactPayload = {
        personId: profile.personId,
        trustedContact: [
          {
            firstName,
            lastName,
            phone: phone.replace(/\D/g, ""),
            phoneAttributes: {
              canReceiveVoiceCalls: true,
              canReceiveTexts: true,
              noPhoneNumber: true,
              notificationOn: true,
              communicationTypes: {
                informative: true,
                promotional: true,
              },
            },
            email,
            emailAttributes: {
              noEmailAddress: true,
              notificationOn: true,
              communicationTypes: {
                informative: true,
                promotional: true,
              },
            },
            relationship,
            relationshipName: "",
            priority: 0,
            personAttributesKeyId,
            isActive: true,
          },
        ],
      };

      if (
        isDuplicateTC({
          firstName,
          lastName,
          email,
          phone: phone.replace(/\D/g, ""),
          relationship,
          personAttributesKeyId,
        })
      ) {
        setIsDuplicateTCModalVisible(true);
      } else {
        await UpsertTrustedContactDetails(trustedContactPayload);
        updateTrustedContactsOnAddOrUpdate({
          personId: trustedContactPayload.personId,
          ...trustedContactPayload.trustedContact[0],
        });
      }
    } catch (error) {
      notification.error({
        message: "Failed to Save Trusted Contact",
      });
    } finally {
      setIsLoading(false);
    }
  }

  function isDuplicateTC(newContact: TC_InitialValues) {
    const savedTrustedContacts = trustedContacts.reduce(
      (acc, tc: TrustedContact) => {
        if (tc.personAttributesKeyId !== newContact.personAttributesKeyId) {
          const {
            firstName,
            lastName,
            phone,
            email,
            relationship,
            personAttributesKeyId,
          } = tc;
          acc.push({
            firstName,
            lastName,
            phone,
            email,
            relationship,
            personAttributesKeyId,
          });
        }
        return acc;
      },
      [] as TC_InitialValues[]
    );

    return savedTrustedContacts.some((savedTC: TC_InitialValues) => {
      const isDuplicateContact = (Object.keys(newContact) as Array<
        keyof TC_InitialValues
      >).every((key) => {
        if (!["relationship", "personAttributesKeyId"].includes(key)) {
          return newContact[key] === savedTC[key];
        } else return true;
      });

      return isDuplicateContact;
    });
  }

  async function validateAndCheckForm() {
    const {
      phone,
      email,
      firstName,
      lastName,
      relationship,
    } = form.getFieldsValue(true);

    const isBasicInfoValid = [firstName, lastName, relationship].every(Boolean);
    const isValidPhoneNumber = isValidPhone(phone);
    const isValidEmail = email && isEmailValid(email);

    const isInvalidFieldExists = !(
      isBasicInfoValid &&
      isValidPhoneNumber &&
      isValidEmail
    );

    isInvalidFieldExists
      ? setShouldDisableBtn(true)
      : setShouldDisableBtn(false);
  }

  function handleValuesChange() {
    validateAndCheckForm();
  }

  return (
    <Form
      form={form}
      name="trustedContactForm"
      labelCol={{ span: 24 }}
      initialValues={initialValues}
      onFinish={handleSubmitForm}
      className={`${styles.editProfileForm} ${styles.padding24}`}
      onValuesChange={handleValuesChange}
      requiredMark={false}
    >
      <Row className={`${textStyles.normalText14} ${styles.marginDown16}`}>
        A trusted contact is a person who will receive communication about your
        transportation & other services.
      </Row>

      <Form.Item label="First name" name="firstName">
        <Input maxLength={100} placeholder="John" />
      </Form.Item>

      <Form.Item label="Last name" name="lastName">
        <Input maxLength={100} placeholder="Smith" />
      </Form.Item>

      <Form.Item label="Phone number" name="phone">
        <InputMask
          mask="(999) 999-9999"
          maskPlaceholder=""
          placeholder="(555) 123-4567"
          onChange={() => {}}
        />
      </Form.Item>

      <Form.Item label="Email" name="email">
        <Input placeholder="name@email.com" />
      </Form.Item>

      <Form.Item label="Relationship" name="relationship">
        <Select
          className={styles.relationshipSelect}
          placeholder="Select"
          suffixIcon={<img src={dropDownIcon} alt="select_reason_dropdown" />}
          options={relationshipTypes}
        />
      </Form.Item>

      <Row
        justify="end"
        className={buttonStyles.drawerFooterBtnsContainer}
        gutter={[16, 16]}
      >
        <Col>
          <Button
            className={`${buttonStyles.defaultWhiteBtn} ${textStyles.normalText16}`}
            onClick={handleOnClose}
          >
            Cancel
          </Button>
        </Col>
        <Col>
          <Button
            htmlType="submit"
            loading={isLoading}
            disabled={shouldDisableBtn}
            className={`${buttonStyles.primaryBlueBtn} ${
              textStyles.normalText16
            } ${shouldDisableBtn && styles.disableBtn}`}
          >
            Save
          </Button>
        </Col>
      </Row>

      <DuplicateTrustedContactModal
        form={form}
        visible={isDuplicateTCModalVisible}
        serviceUserfirstName={profile.firstName}
        setVisibility={setIsDuplicateTCModalVisible}
        validateForm={validateAndCheckForm}
      />
    </Form>
  );
}

export default TrustedContactForm;
